import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import api from "../../../api/resources";

export const TreasuryContext = createContext(null);

export function TreasuryProvider({ children }) {
  const [accounts, setAccounts] = useState(null);
  const [accountFilter, dispatchAccountFilter] = useReducer(
    AccountFilterReducer,
    initialAccountFilter,
  );
  const [monthlyCashFlows, setMonthlyCashFlows] = useState(null);
  const [dailyBalances, setDailyBalances] = useState(null);
  const [loanTransactions, setLoanTransactions] = useState(null);
  const [loading, setLoading] = useState(true);

  const initAccounts = async () => {
    const response = await api.getAccounts();
    if (response.ok) {
      const jsonResponse = await response.json();
      setAccounts(jsonResponse);
    }
  };

  const initMonthlyCashFlows = async (accountID = null) => {
    const response = await api.getMonthlyCashFlows(accountID);
    if (response.ok) {
      const jsonResponse = await response.json();
      setMonthlyCashFlows(jsonResponse);
    }
  };

  const initDailyCashFlowBalances = async (accountID = null) => {
    const response = await api.getDailyCashFlowBalances(accountID);
    if (response.ok) {
      const jsonResponse = await response.json();
      setDailyBalances(jsonResponse);
    }
  };

  const initLoanTransactions = async () => {
    const response = await api.getLoanTransactions();
    if (response.ok) {
      const jsonResponse = await response.json();
      setLoanTransactions(jsonResponse);
    }
  };

  const init = async () => {
    setLoading(true);
    await initAccounts();
    await initDailyCashFlowBalances();
    await initMonthlyCashFlows();
    await initLoanTransactions();
    dispatchAccountFilter({ type: "clear" });
    setLoading(false);
  };

  useEffect(() => {
    init();
  }, []);

  const handleFilter = async (filter) => {
    setLoading(true);
    initDailyCashFlowBalances(filter);
    initMonthlyCashFlows(filter);
    setLoading(false);
  };

  useEffect(() => {
    if (accountFilter) {
      handleFilter(accountFilter.value);
    } else {
      dispatchAccountFilter({ type: "clear" });
      init();
    }
  }, [accountFilter]);

  const value = {
    loanTransactions,
    accounts,
    accountFilter,
    dispatchAccountFilter,
    monthlyCashFlows,
    dailyBalances,
    init,
    loading: loading,
  };

  return <TreasuryContext value={value}>{children}</TreasuryContext>;
}

export function useTreasury() {
  return useContext(TreasuryContext);
}

function AccountFilterReducer(existingAccountFilter, action) {
  const { type } = action;

  switch (type) {
    case "add": {
      // `accountFilter` contains the select option `{label: "", value:""}`
      return action.accountFilter;
    }

    case "clear": {
      return null;
    }

    default: {
      throw new TypeError(`Unknown action "${action.type}"`);
    }
  }
}

const initialAccountFilter = null;
